import { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Alert } from '@mui/material';
import { Control, FieldValues, useForm } from 'react-hook-form';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { Order } from '../../../app/models/order';
import AppDropzone from '../../../app/components/AppDropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import agent from '../../../app/api/agent';
import { useAppDispatch } from '../../../app/store/configureStore';
import { fetchAdminOrderAsync } from './adminOrdersSlice';
import { CLOUDINARY_MAX_FILE_SIZE_BYTES, singleFileTooLargeMessage } from '../../../app/constants/fileUpload';

interface Props {
    order: Order;
    fileType: 'test' | 'key';
}

// Dodaj interfejs dla pliku, aby TypeScript mógł rozpoznać właściwości
interface FileWithPreview extends File {
    preview?: string;
}

export default function AdminTestFileUploader({ order, fileType }: Props) {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const dispatch = useAppDispatch();

    const { control, handleSubmit, watch, reset } = useForm({
        defaultValues: {
            file: null as FileWithPreview | null,
        }
    });

    // Użyj poprawnego typu dla file
    const watchFile = watch('file') as FileWithPreview | null;

    // Określenie tytułu i nazwy przycisku na podstawie typu pliku
    const title = fileType === 'test' ? 'Dodaj arkusz egzaminacyjny' : 'Dodaj klucz odpowiedzi';
    const buttonText = fileType === 'test' ? 'Arkusz' : 'Klucz';
    const buttonTitle = fileType === 'test' ? 'Dodaj arkusz egzaminacyjny' : 'Dodaj klucz odpowiedzi';

    // Ścieżka do pliku w zależności od typu
    const currentFilePath = fileType === 'test' ? order.packageTestPath : order.packageTestKeyPath;

    const handleClickOpen = () => {
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        reset();
    };

    const onSubmit = async (data: FieldValues) => {
        if (!data.file) {
            toast.error('Proszę wybrać plik');
            return;
        }
    
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append('File', data.file as File);
            // Użyj publicId zamiast id
            formData.append('OrderId', order.publicId.toString());
            formData.append('Type', fileType);
    
            // Wywołaj endpoint API
            await agent.Admin.Orders.uploadTestFile(formData);
            
            toast.success(`Plik ${fileType === 'test' ? 'arkusza' : 'klucza'} został pomyślnie przesłany`);
            // Zmień id na publicId
            dispatch(fetchAdminOrderAsync(order.publicId));
            handleClose();
        } catch (error) {
            console.error('Błąd podczas przesyłania pliku:', error);
        } finally {
            setLoading(false);
        }
    };

    // Określ czy jest to obraz czy PDF
    const isImage = currentFilePath && 
        (currentFilePath.toLowerCase().endsWith('.jpg') || 
         currentFilePath.toLowerCase().endsWith('.jpeg') || 
         currentFilePath.toLowerCase().endsWith('.png') ||
         currentFilePath.toLowerCase().endsWith('.webp'));

    const isPdf = currentFilePath && currentFilePath.toLowerCase().endsWith('.pdf');

    return (
        <>
            <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudUploadIcon />}
                onClick={handleClickOpen}
                size="small"
                title={buttonTitle}
                sx={{ mx: 1, height: '100%' }}
            >
                {buttonText}
            </Button>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <form id="uploadForm" onSubmit={handleSubmit(onSubmit)}>
                        <Typography variant="body1" sx={{ mb: 3 }}>
                            Wybierz plik, który chcesz przesłać jako {fileType === 'test' ? 'arkusz egzaminacyjny' : 'klucz odpowiedzi'}.
                        </Typography>
                        
                        {currentFilePath && (
                            <Box sx={{ mb: 3 }}>
                                <Alert severity="info" sx={{ mb: 1 }}>
                                    Istnieje już plik {fileType === 'test' ? 'arkusza egzaminacyjnego' : 'klucza odpowiedzi'}. Przesłanie nowego pliku zastąpi obecny.
                                </Alert>
                                
                                {isPdf ? (
                                    <Button 
                                        component="a" 
                                        href={currentFilePath} 
                                        target="_blank"
                                        variant="text" 
                                        color="primary"
                                        sx={{ mt: 1, textTransform: 'none' }}
                                    >
                                        Otwórz obecny plik PDF w nowej karcie
                                    </Button>
                                ) : isImage ? (
                                    <Box>
                                        <Typography variant="body2" sx={{ mt: 1 }}>Podgląd obecnego pliku:</Typography>
                                        <img
                                            src={currentFilePath}
                                            alt={fileType === 'test' ? 'Podgląd arkusza' : 'Podgląd klucza'}
                                            style={{ maxWidth: '100%', maxHeight: '200px', display: 'block', margin: '8px 0' }}
                                        />
                                    </Box>
                                ) : currentFilePath ? (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Obecny plik: {currentFilePath.split('/').pop() || currentFilePath}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                        
                        <AppDropzone
                            name="file"
                            control={control as unknown as Control<FieldValues>}
                            acceptedFormats="pdf,jpg,jpeg,png"
                            maxSizeBytes={CLOUDINARY_MAX_FILE_SIZE_BYTES}
                            tooLargeMessage={singleFileTooLargeMessage}
                        />

                        {watchFile && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                Wybrany plik: {watchFile.name}
                            </Alert>
                        )}
                    </form>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Anuluj</Button>
                    <LoadingButton 
                        loading={loading}
                        disabled={!watchFile}
                        type="submit"
                        form="uploadForm"
                        variant="contained"
                        color="primary"
                    >
                        Prześlij
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
